import { Card, Text } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useEffect, useMemo, useState } from 'react';
import type { IProcessAction } from '../../../../common/interfaces/IProcessAction';
import type { IQuestionnaireItem, IResponseItem } from '../../../../common/interfaces/IQuestionnaire';
import type { ValidationErrors } from '../../../utils/validation';
import { PatientDataError } from '../../../../common/enums/PatientDataError';
import { ProcessActivityStatus } from '../../../../common/enums/ProcessActivityStatus';
import { questionsPerQuestionnaireStep } from '../../../../common/enums/QuestionnaireName';
import { getQuestionnaireSchema } from '../../../../common/validation/customControls/osasFlex';
import { fetchJson } from '../../../store/fetch';
import { useReloadPatientAvailableActions } from '../../../utils/hooks/useReloadPatientAvailableActions';
import { emptyValidationErrors, validate } from '../../../utils/validation';
import { Step } from '../../elements/atoms/step/Step';
import { CustomContainer } from '../../elements/molecules/customContainer/CustomContainer';
import { Stack } from '../../elements/molecules/stack/Stack';
import { StyledFormGroup } from '../../elements/organisms/StyledFormGroup/StyledFormGroup';
import { scrollToValidationError } from '../../elements/scrollToValidationError';
import { QuestionnaireAnswerOptions } from './QuestionnaireAnswerOptions';
import { QuestionnaireFormButtons } from './QuestionnaireFormButtons';

interface IQuestionnaireFormProps {
    action: IProcessAction;
    page: number;
    updateProgress: (progress: number) => void;
}

export function QuestionnaireForm(props: IQuestionnaireFormProps) {
    const { action, updateProgress } = props;
    const navigate = useNavigate();
    const reloadPatientAvailableActions = useReloadPatientAvailableActions();
    const [currentPage, setCurrentPage] = useState(props.page);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [nrOfPages, setNrOfPages] = useState(0);
    const [questionnaireItems, setQuestionnaireItems] = useState<IQuestionnaireItem[]>([]);
    const [answers, setAnswers] = useState<{ [keys: string]: string }>({});

    const questionnaireName = action?.serviceWorkflowStep.parameters?.questionnaireName;
    const processId = action?.processId;
    const processActivityId = action?.activities[0]?.processActivityId;

    const questionsOnThisPage = useMemo(() => {
        if (questionnaireName === undefined) {
            return [];
        }

        return questionnaireItems
            .slice(
                (currentPage - 1) * questionsPerQuestionnaireStep[questionnaireName],
                currentPage * questionsPerQuestionnaireStep[questionnaireName]
            )
            .map((item, index) => ({
                ...item,
                number: (currentPage - 1) * questionsPerQuestionnaireStep[questionnaireName] + index + 1,
            }));
    }, [questionnaireItems, currentPage]);

    useEffect(() => {
        scrollToNextQuestion();
    }, [currentPage]);

    useEffect(() => {
        if (questionnaireName) {
            setNrOfPages(Math.ceil(questionnaireItems.length / questionsPerQuestionnaireStep[questionnaireName]));
        }
    }, [questionnaireItems]);

    useEffect(() => {
        if (errors.size > 0) {
            scrollToValidationError(errors.get('answers') ?? '');
        }
    }, [errors]);

    useEffect(() => {
        updateProgress((Object.keys(answers).length * 100) / questionnaireItems.length);
    }, [answers, questionnaireItems]);

    useEffect(() => {
        if (questionnaireName && processId) {
            getPatientQuestionnaire();
        }
    }, [questionnaireName, processId]);

    if (!questionnaireName || !processId) {
        return null;
    }

    return (
        <CustomContainer>
            <Card>
                <Card.Body>
                    <Text
                        fontWeight="bold"
                        pb={4}
                        pt={4}
                        size="2xl"
                    >{`${getQuestionnaireCardTitle(action.label)} (${currentPage}/${nrOfPages})`}</Text>
                    <Text>
                        Weet je het antwoord op een vraag niet? Vraag het eventueel aan je partner, of vul in hoe je
                        denkt dat het is.
                    </Text>
                </Card.Body>
            </Card>

            {questionsOnThisPage.map((question) => {
                return (
                    <Stack
                        className={answers[question.prefix] ? '' : `${questionnaireName} empty ${question.prefix}`}
                        key={question.prefix}
                    >
                        <Step>
                            {question.number}/{questionnaireItems.length}
                        </Step>
                        <StyledFormGroup
                            hasErrors={!answers[question.prefix] && errors.has('answers')}
                            label={question.text}
                            layout="vertical"
                        >
                            <QuestionnaireAnswerOptions
                                errors={errors}
                                onChange={onChangeAnswer}
                                questionItem={question}
                                value={answers[question.prefix] || ''}
                            />
                        </StyledFormGroup>
                    </Stack>
                );
            })}

            <QuestionnaireFormButtons
                currentPage={currentPage}
                goToNextPage={goToNextPage}
                goToPreviousPage={goToPreviousPage}
                nrOfPages={nrOfPages}
                sendQuestionnaire={sendQuestionnaire}
            />
        </CustomContainer>
    );

    function goToNextPage() {
        const hasErrors = validateQuestionnaireResponse(ProcessActivityStatus.draft);

        if (!hasErrors) {
            setCurrentPage(currentPage + 1);
        }
    }

    function goToPreviousPage() {
        setCurrentPage(currentPage - 1);
    }

    function onChangeAnswer(update: { [key: string]: string }) {
        const newAnswers = { ...answers, ...update };

        setAnswers(newAnswers);
        save(ProcessActivityStatus.draft, newAnswers);
    }

    function sendQuestionnaire() {
        save(ProcessActivityStatus.complete, answers);
    }

    async function getPatientQuestionnaire() {
        try {
            let url = `/api/patient/process/${processId}/questionnaire/${questionnaireName}`;
            if (processActivityId) {
                url += `?processActivityId=${processActivityId}`;
            }
            const result = await fetchJson(url, 'GET');

            if (result.json.questionnaire) {
                setQuestionnaireItems(result.json.questionnaire.items);
            }
            if (result.json.responseItems) {
                setAnswers(
                    result.json.responseItems.reduce(
                        (newAnswers: { [keys: string]: string }, item: IResponseItem) => ({
                            ...newAnswers,
                            [item.prefix]: item.value,
                        }),
                        {}
                    )
                );
            }
        } catch {
            navigate(`/saml/login?error=${PatientDataError.invalidUrl}`);
        }
    }

    async function save(mode: ProcessActivityStatus, questionnaireAnswers: { [keys: string]: string }) {
        if (mode === ProcessActivityStatus.complete) {
            const hasErrors = validateQuestionnaireResponse(mode);

            if (hasErrors) {
                return false;
            }
        }

        const result = await fetchJson(`/api/patient/processes/${processId}`, 'POST', {
            actionId: action.id,
            activityData: {
                answers: questionnaireAnswers,
            },
            processWorkflowStepId: action.processWorkflowStepId,
            serviceWorkflowStepId: action.serviceWorkflowStep.id,
            status: mode,
        });

        if (result.status === 200) {
            if (!action.processWorkflowStepId || mode === ProcessActivityStatus.complete) {
                await reloadPatientAvailableActions();

                if (mode === ProcessActivityStatus.complete) {
                    navigate(`/osas/questionnaires/${encodeURIComponent(action.label)}/success`);
                }
            }
        }
    }

    function validateQuestionnaireResponse(mode: ProcessActivityStatus) {
        if (!questionnaireName) {
            return;
        }

        const maxAnswers = questionnaireItems.length;
        const minAnswers =
            mode === ProcessActivityStatus.complete
                ? maxAnswers
                : Math.min(currentPage * questionsPerQuestionnaireStep[questionnaireName], maxAnswers);

        const validationSchema = getQuestionnaireSchema(minAnswers, maxAnswers);

        const validationResult = validate(validationSchema, { answers });

        setErrors(validationResult.errors);

        return validationResult.hasErrors;
    }

    function scrollToNextQuestion() {
        const emptyQuestions = document.getElementsByClassName('empty');

        if (emptyQuestions.length > 0) {
            emptyQuestions[0].scrollIntoView({ block: 'center', inline: 'start' });
        }
    }

    function getQuestionnaireCardTitle(label: string) {
        return label === 'ess' ? 'Je huidige leefwijze en klachten' : 'Slaapklachten afgelopen 3 maanden';
    }
}
